import { useEffect, useRef, useState } from 'react';
import {
  AmplifyChannelCreatorParams,
  amplifyChannelEventExpiryTime,
  AmplifyEventType,
  AmplifyEventTypePayload
} from './amplifyEventTypes';
import { useAmplifyPublishEvent } from './useAmplifyPublishEvent';

export const useAmplifyPublishEventInterval = <T extends AmplifyEventType>(
  props?: {
    channelType: T;
    channelParams: AmplifyChannelCreatorParams[T];
    payload: AmplifyEventTypePayload[T];
  } | null
) => {
  // NOTE: This is a fix due to incorrect rendering on parent components
  const [intervalProps, setIntervalProps] = useState<{
    channelType: T;
    channelParams: AmplifyChannelCreatorParams[T];
    payload: AmplifyEventTypePayload[T];
  } | null>();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const propsStringRef = useRef<string | null>(null);
  const { publishEvent } = useAmplifyPublishEvent(props);

  useEffect(() => {
    const propsString = props ? JSON.stringify(props) : null;
    const intervalPropsString = intervalProps
      ? JSON.stringify(intervalProps)
      : null;

    // Only update if the string representation is different
    if (propsString === intervalPropsString) return;

    // Store the current props string for comparison
    propsStringRef.current = propsString;

    // Update the interval props
    setIntervalProps(props);
  }, [props, intervalProps]);

  useEffect(() => {
    if (!intervalProps) return;

    // Skip if we already have an interval running
    if (intervalRef.current) return;

    publishEvent(intervalProps.payload);

    intervalRef.current = setInterval(() => {
      publishEvent(intervalProps.payload);
    }, amplifyChannelEventExpiryTime[intervalProps.channelType]);

    return () => {
      if (!intervalRef.current) return;
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [intervalProps]);
};
